// charts-builders.ts
import { baseOpts } from '../../core/charts-core.js';

const COLORS_STACK = { wins: '#10b981', draws: '#f59e0b', losses: '#ef4444' };
const N = (v) => (Number.isFinite(+v) ? +v : 0);
const PCT = (v, total) => {
  if (total) return Math.round((v / total) * 1000) / 10;
  return 0;
};

export const buildTeamPoints = (teams, dark) => {
  const categories = teams.map(t => t.teamName);
  const points = teams.map(t => N(t.points));
  const legDiff = teams.map(t => N(t.legsWon) - N(t.legsLost));
  const winRate = teams.map(t => PCT(N(t.wins), N(t.matches)));

  return baseOpts(dark, {
    chart: { ...baseOpts(dark).chart, type: 'line', background: 'transparent' },
    series: [
      { name: 'Points', type: 'column', data: points },
      { name: 'Win %', type: 'line', data: winRate },
      { name: 'Leg Diff', type: 'line', data: legDiff },
    ],
    stroke: { width: [0, 3, 2], curve: 'smooth', dashArray: [0, 0, 5] },
    markers: { size: [0, 4, 3], hover: { sizeOffset: 2 } },
    xaxis: { categories },
    yaxis: [
      { title: { text: 'Points' }, min: 0, forceNiceScale: true, decimalsInFloat: 0 },
      { opposite: true, title: { text: 'Win % / Leg Diff' }, forceNiceScale: true, max: 100 },
    ],
    plotOptions: { bar: { borderRadius: 8, columnWidth: '48%', borderRadiusApplication: 'end' } },
    colors: ['#38bdf8', '#22c55e', '#f59e0b'],
    fill: { type: ['solid', 'gradient', 'solid'], gradient: { shadeIntensity: 0.2, opacityFrom: 0.55, opacityTo: 0.2 } },
    dataLabels: { enabled: false },
    legend: { position: 'bottom' },
    tooltip: {
      shared: true,
      y: [
        { formatter: (v) => `${Math.round(v || 0)} pts` },
        { formatter: (v) => `${(v || 0).toFixed(1)}%` },
        { formatter: (v) => `${Math.round(v || 0)} legs` },
      ],
    },
  });
};

function totalTonsFor(p) {
  return N(p.tons) || 0;
}

export const buildPlayerBestTon = (players, dark) => {
  const ranked = [...players].sort((a, b) =>
    ((N(b.legsWon) * 2 + N(b.closes) * 3 + totalTonsFor(b)) - (N(a.legsWon) * 2 + N(a.closes) * 3 + totalTonsFor(a))) ||
    (N(b.closes) - N(a.closes)) ||
    (N(b.legsWon) - N(a.legsWon)) ||
    (totalTonsFor(b) - totalTonsFor(a))
  );
  const top = ranked.slice(0, 8);
  const names = top.map(p => {
    const team = p.teamName ? ` • ${p.teamName}` : '';
    return `${p.playerName || p.fullName || '-'}${team}`;
  });

  return baseOpts(dark, {
    chart: { ...baseOpts(dark).chart, background: 'transparent' },
    series: [
      { name: 'Legs Won', data: top.map(p => N(p.legsWon)) },
      { name: 'Checkouts', data: top.map(p => N(p.closes)) },
      { name: 'Tons', data: top.map(totalTonsFor) },
      { name: 'Highest CO', data: top.map(p => N(p.highestCheckout)) },
    ],
    yaxis: { categories: names },
    xaxis: { min: 0, forceNiceScale: true, decimalsInFloat: 0 },
    plotOptions: { bar: { horizontal: true, borderRadius: 6, barHeight: '58%' } },
    colors: ['#38bdf8', '#22c55e', '#a78bfa', '#f59e0b'],
    dataLabels: { enabled: false },
    legend: { position: 'bottom' },
    tooltip: {
      shared: true,
      intersect: false,
      y: [
        { formatter: (v) => `${Math.round(v || 0)} legs` },
        { formatter: (v) => `${Math.round(v || 0)} checkouts` },
        { formatter: (v) => `${Math.round(v || 0)} tons` },
        { formatter: (v) => `${Math.round(v || 0)} high CO` },
      ],
    },
  });
};

// Turn-based rounds chart disabled. Official stats are scoresheet-derived only.
// export const buildRoundsArea = () => ({});

export const buildTeamsBigsStacked = (teams, dark) => {
  const wins = teams.map(t => N(t.wins));
  const draws = teams.map(t => N(t.draws));
  const losses = teams.map(t => N(t.losses));
  const matches = teams.map(t => N(t.matches));

  return baseOpts(dark, {
    series: [
      { name: 'Wins %', data: teams.map((_, i) => PCT(wins[i], matches[i])) },
      { name: 'Draws %', data: teams.map((_, i) => PCT(draws[i], matches[i])) },
      { name: 'Losses %', data: teams.map((_, i) => PCT(losses[i], matches[i])) },
    ],
    xaxis: { categories: teams.map(t => t.teamName) },
    yaxis: { min: 0, max: 100, tickAmount: 5, labels: { formatter: (v) => `${Math.round(v)}%` } },
    plotOptions: { bar: { columnWidth: '58%', borderRadius: 7 } },
    dataLabels: { enabled: false },
    chart: { ...baseOpts(dark).chart, background: 'transparent', stacked: true, stackType: '100%' },
    colors: [COLORS_STACK.wins, COLORS_STACK.draws, COLORS_STACK.losses],
    legend: { position: 'bottom' },
    tooltip: {
      shared: true,
      y: {
        formatter: (value, ctx) => {
          const i = ctx?.dataPointIndex ?? 0;
          const s = ctx?.seriesIndex ?? 0;
          let raw = losses[i];
          if (s === 0) raw = wins[i];
          else if (s === 1) raw = draws[i];
          return `${(value || 0).toFixed(1)}% (${raw})`;
        },
      },
    },
  });
};
